import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import BallsDisplay from '../../../LottoAssistant/components/BallsDisplay';
import BetStatusBadge from '../../../LottoAssistant/components/BetStatusBadge';
import { formatLottoDate } from '../../../LottoAssistant/utils/date';
import {
  isBallBasedMarket,
  formatOdds,
} from '../../../LottoAssistant/utils/common';
import { formatMoney } from '../../utils';
import { ticketBetToRow } from './utils';
import classes from './TicketCard.module.scss';

const ArrowIcon = () => (
  <svg
    width="7"
    height="13"
    viewBox="0 0 7 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M0.970131 12.5C1.23775 12.5 1.46356 12.4079 1.64755 12.2238L6.65711 7.26151C6.89128 7.03556 7 6.79289 7 6.5C7 6.20711 6.89128 5.95607 6.66547 5.73849L1.64755 0.776151C1.46356 0.59205 1.23775 0.5 0.970131 0.5C0.434886 0.5 0 0.926778 0 1.46234C0 1.73013 0.108722 1.9728 0.301075 2.17364L4.70012 6.50837L0.301075 10.8347C0.108722 11.0272 0 11.2699 0 11.5377C0 12.0732 0.434886 12.5 0.970131 12.5Z"
    />
  </svg>
);

const QrIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M2.5 9.16667H4.16667V10.8333H2.5V9.16667ZM9.16667 4.16667H10.8333V7.5H9.16667V4.16667ZM7.5 9.16667H10.8333V12.5H9.16667V10.8333H7.5V9.16667ZM12.5 9.16667H14.1667V10.8333H15.8333V9.16667H17.5V10.8333H15.8333V12.5H17.5V15.8333H15.8333V17.5H14.1667V15.8333H10.8333V17.5H9.16667V14.1667H12.5V12.5H14.1667V10.8333H12.5V9.16667ZM15.8333 15.8333V12.5H14.1667V15.8333H15.8333ZM12.5 2.5H17.5V7.5H12.5V2.5ZM14.1667 4.16667V5.83333H15.8333V4.16667H14.1667ZM2.5 2.5H7.5V7.5H2.5V2.5ZM4.16667 4.16667V5.83333H5.83333V4.16667H4.16667ZM2.5 12.5H7.5V17.5H2.5V12.5ZM4.16667 14.1667V15.8333H5.83333V14.1667H4.16667Z"
    />
  </svg>
);

const TicketCard = ({ slip }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const rows = slip.ticket_bets.map(ticketBetToRow);

  return (
    <>
      <div className={classes.Card}>
        <div
          className={classes.Header}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <div className={classes.HeaderInfo}>
            <p className={classes.DateText}>
              {formatLottoDate(slip.inserted_at).replace(' ', ' · ')}
            </p>
          </div>
          <button
            type="button"
            className={classes.IconButton}
            onClick={(e) => {
              e.stopPropagation();
              setQrOpen(true);
            }}
            aria-label="Show QR code"
          >
            <QrIcon />
          </button>
          <button
            type="button"
            className={
              classes.IconButton +
              ' ' +
              (isExpanded ? classes.ArrowExpanded : classes.ArrowCollapsed)
            }
            aria-label="Toggle"
          >
            <ArrowIcon />
          </button>
        </div>

        <div className={classes.StakeSection}>
          <div className={classes.StakeCol}>
            <p className={classes.FooterLabel}>STAKE</p>
            <p className={classes.StakeValue}>
              {formatMoney(slip.stake, 'ZAR')}
            </p>
          </div>
          <div className={classes.BetsCol}>
            <p className={classes.FooterLabel}>BETS</p>
            <p className={classes.BetsValue}>{slip.total_wagers}</p>
          </div>
          <div className={classes.PayoutCol}>
            <p className={classes.FooterLabel}>POTENTIAL PAYOUT</p>
            <p className={classes.PayoutValue}>
              {formatMoney(slip.potential_payout, 'ZAR')}
            </p>
          </div>
        </div>

        <div
          className={classes.Collapse + ' ' + (isExpanded ? classes.Open : '')}
        >
          <div className={classes.Divider} />
          {rows.map((row, i) => (
            <React.Fragment key={row.key}>
              <div className={classes.TicketBetRowOuter}>
                <div className={classes.TicketBetRow}>
                  <span className={classes.TicketBetIndex}>{i + 1}</span>

                  {isBallBasedMarket(row.outcomeType) ? (
                    <div className={classes.BallMarketRow}>
                      <BallsDisplay numbers={row.balls} size="small" gap={4} />
                      <p
                        className={classes.TicketBetMarket}
                        style={{ flexGrow: 0, textAlign: 'right' }}
                      >
                        {row.marketTitle}
                      </p>
                    </div>
                  ) : (
                    <p className={classes.TicketBetMarket}>
                      {row.marketTitle} · {row.outcomeTitle}
                    </p>
                  )}
                </div>

                <div className={classes.InnerDivider} />

                <div className={classes.TicketBetFooter}>
                  <div className={classes.TicketBetFooterCol}>
                    <p className={classes.TicketLabel}>STAKE</p>
                    <p className={classes.TicketValue}>
                      {formatMoney(row.stake, 'ZAR')}
                    </p>
                  </div>

                  <div className={classes.TicketBetFooterCol}>
                    <p className={classes.TicketLabel}>ODDS</p>
                    <p className={classes.TicketValue}>
                      {formatOdds(row.odds)}
                    </p>
                  </div>

                  <BetStatusBadge variant={row.status} />
                </div>
              </div>
              <div className={classes.Divider} />
            </React.Fragment>
          ))}

          <div className={classes.TicketIdRow}>
            <p className={classes.TicketId}>Ticket ID: {slip.bet_slip_id}</p>
          </div>
        </div>
      </div>

      {qrOpen && (
        <div
          className={classes.QrModalOverlay}
          onClick={() => setQrOpen(false)}
        >
          <div
            className={classes.QrModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <QRCodeSVG value={slip.bet_slip_id} size={250} />
            <p className={classes.TicketId}>Ticket ID: {slip.bet_slip_id}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default TicketCard;
